import api from './api.service';
import type { RejectionPayload } from './rejection.service';

export type VatConfigurationOption = {
  id: string;
  vat_type: string;
  vat_rate: number;
  description?: string;
  gl_account_id?: string | null;
  gl_account?: { id: string; account_code: string; account_name: string } | null;
};

export type DirectExpenseLineInput = {
  expense_account_id: string;
  item_description?: string;
  quantity?: number;
  uom?: string;
  unit_price?: number;
  amount?: number;
  vat_configuration_id?: string;
  input_vat_account_id?: string;
  vat_rate?: number;
  tax_inclusive?: boolean;
  tax_amount?: number;
  project_id?: string;
  cost_center?: string;
  department?: string;
  line_remarks?: string;
  /** Item-wise technical specification (plain multi-line text) */
  specifications?: string | null;
  expense_treatment?: ExpenseTreatment;
  prepaid_asset_account_id?: string | null;
  prepaid_config?: PrepaidLineConfig | null;
};

export type ExpenseTreatment = 'NORMAL_EXPENSE' | 'PREPAID_EXPENSE';

/** Recognition settings for a prepaid line; the contract is created when the invoice posts. */
export type PrepaidLineConfig = {
  service_start_date?: string;
  service_end_date?: string;
  recognition_method?: string | null;
  day_count_method?: string | null;
  partial_month_policy?: string | null;
  posting_mode?: string | null;
  category_id?: string | null;
};

export interface DirectPurchaseInvoice {
  id: string;
  invoice_number: string;
  invoice_type: string;
  supplier_id: string;
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  received_date: string;
  due_date: string;
  posting_date?: string;
  payment_terms?: string;
  branch_id?: string;
  department?: string;
  department_id?: string | null;
  cost_center?: string;
  project_id?: string;
  currency: string;
  debit_account_id?: string;
  subtotal: number;
  discount_amount: number;
  vat_amount: number;
  freight_charges: number;
  other_charges: number;
  rounding_difference: number;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
  payment_status: string;
  invoice_status: string;
  approval_status?: string;
  approval_instance_id?: string | null;
  gl_posted: boolean;
  journal_voucher_id?: string;
  journalVoucher?: { id: string; voucher_number?: string; voucher_date?: string; status?: string };
  notes?: string;
  supplier?: { supplier_name: string; supplier_code?: string };
  debitAccount?: { account_code: string; account_name: string };
  lines?: Array<{
    id: string;
    line_number: number;
    item_description?: string;
    specifications?: string | null;
    quantity?: number;
    unit_price?: number;
    amount: number;
    tax_amount: number;
    line_subtotal: number;
    tax_inclusive: boolean;
    expense_account_id: string;
    vat_configuration_id?: string;
    vat_rate?: number;
    project_id?: string;
    project?: { project_number?: string; project_name?: string };
    expenseAccount?: { account_code: string; account_name: string };
    vatConfiguration?: {
      vat_type: string;
      vat_rate: number;
      description?: string;
    };
    input_vat_account_id?: string;
    inputVatAccount?: { account_code: string; account_name: string };
    expense_treatment?: string;
    prepaid_asset_account_id?: string | null;
    prepaid_config?: PrepaidLineConfig | string | null;
    recognition_contract_id?: string | null;
  }>;
  attachments?: Array<{ id: string; file_name: string; mime_type?: string | null; file_size?: number | null; created_at: string }>;
}

export interface DirectPurchaseInvoicePayment {
  kind: 'Payment' | 'Advance adjusted' | 'Debit note';
  payment_voucher_id?: string;
  debit_note_id?: string;
  reference: string;
  date: string | null;
  status: string | null;
  payment_mode: string | null;
  amount: number;
  settlement_payment_voucher_id?: string | null;
  settlement_voucher_number?: string | null;
}

export type CreateDirectPurchaseInvoicePayload = {
  supplier_id: string;
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  received_date: string;
  due_date: string;
  posting_date?: string;
  payment_terms?: string;
  debit_account_id?: string;
  currency?: string;
  branch_id?: string;
  department?: string;
  department_id?: string;
  cost_center?: string;
  project_id?: string;
  notes?: string;
  discount_amount?: number;
  freight_charges?: number;
  other_charges?: number;
  rounding_difference?: number;
  lines: DirectExpenseLineInput[];
};

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

class DirectPurchaseInvoiceService {
  async list(params?: Record<string, string | number | undefined>): Promise<DirectPurchaseInvoice[]> {
    const q = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') q.append(k, String(v));
      });
    }
    const res = await api.get(`/finance/direct-purchase-invoices?${q.toString()}`);
    const raw = unwrap<DirectPurchaseInvoice[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getRegister(params?: Record<string, string | undefined>) {
    const q = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => v && q.append(k, v));
    const res = await api.get(`/finance/direct-purchase-invoices/register?${q.toString()}`);
    return unwrap<any[]>(res);
  }

  async getVatConfigurations(): Promise<VatConfigurationOption[]> {
    const res = await api.get('/finance/direct-purchase-invoices/vat-configurations');
    const raw = unwrap<unknown>(res);
    if (Array.isArray(raw)) return raw as VatConfigurationOption[];
    if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown[] }).data)) {
      return (raw as { data: VatConfigurationOption[] }).data;
    }
    return [];
  }

  async checkDuplicate(supplierId: string, supplierInvoiceNumber: string, excludeId?: string) {
    const q = new URLSearchParams({ supplier_id: supplierId, supplier_invoice_number: supplierInvoiceNumber });
    if (excludeId) q.append('exclude_id', excludeId);
    const res = await api.get(`/finance/direct-purchase-invoices/check-duplicate?${q.toString()}`);
    return unwrap<{ duplicate: boolean; policy: string }>(res);
  }

  async getById(id: string): Promise<DirectPurchaseInvoice> {
    const res = await api.get(`/finance/direct-purchase-invoices/${id}`);
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async create(payload: CreateDirectPurchaseInvoicePayload) {
    const res = await api.post('/finance/direct-purchase-invoices', payload);
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async update(id: string, payload: Partial<CreateDirectPurchaseInvoicePayload>) {
    const res = await api.put(`/finance/direct-purchase-invoices/${id}`, payload);
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async submit(id: string) {
    const res = await api.post(`/finance/direct-purchase-invoices/${id}/submit`, {});
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async approve(id: string, remarks?: string) {
    const res = await api.post(`/finance/direct-purchase-invoices/${id}/approve`, { remarks });
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async rejectApproval(id: string, payload: RejectionPayload) {
    const res = await api.post(`/finance/direct-purchase-invoices/${id}/reject-approval`, payload);
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async post(id: string) {
    const res = await api.post(`/finance/direct-purchase-invoices/${id}/post`, {});
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async unpost(id: string) {
    const res = await api.post(`/finance/direct-purchase-invoices/${id}/unpost`, {});
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async cancel(id: string) {
    const res = await api.post(`/finance/direct-purchase-invoices/${id}/cancel`, {});
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async getPrintData(id: string) {
    const res = await api.get(`/finance/direct-purchase-invoices/${id}/print`);
    return unwrap<DirectPurchaseInvoice>(res);
  }

  async getVoucherByInvoiceId(invoiceId: string) {
    try {
      const res = await api.get('/finance/vouchers/by-reference', {
        params: { reference_type: 'SupplierInvoice', reference_id: invoiceId },
      });
      return unwrap<Record<string, unknown>>(res) ?? null;
    } catch {
      return null;
    }
  }

  async uploadAttachment(id: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post(`/finance/direct-purchase-invoices/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<any>(res);
  }

  async listPayments(id: string): Promise<DirectPurchaseInvoicePayment[]> {
    const res = await api.get(`/finance/direct-purchase-invoices/${id}/payments`);
    return unwrap<DirectPurchaseInvoicePayment[]>(res) ?? [];
  }

  async getAttachmentBlob(attachmentId: string, inline = false): Promise<Blob> {
    const res = await api.getClient().get(`/finance/direct-purchase-invoices/attachments/${attachmentId}/download`, {
      params: inline ? { inline: '1' } : undefined,
      responseType: 'blob',
    });
    return res.data as Blob;
  }

  async deleteAttachment(attachmentId: string) {
    await api.delete(`/finance/direct-purchase-invoices/attachments/${attachmentId}`);
  }
}

export const directPurchaseInvoiceService = new DirectPurchaseInvoiceService();
export default directPurchaseInvoiceService;
